<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import Modal from '@/components/common/layout/Modal.vue'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowPathIcon,
  DocumentDuplicateIcon,
  PlusCircleIcon,
  ArrowsRightLeftIcon,
  ArrowTopRightOnSquareIcon,
  InformationCircleIcon,
} from '@heroicons/vue/24/outline'
import { CheckCircleIcon, ExclamationTriangleIcon, ShieldCheckIcon, ShieldExclamationIcon } from '@heroicons/vue/24/solid'
import { useQuoteStore } from '@/stores/useQuoteStore'
import { useToast } from 'vue-toast-notification'

interface Props {
  showModal: boolean
  selectedChildren: any[]
  formTypeOptions: any[]
  parentFormType: string | null
  isFormReadOnly?: boolean
  parentReference?: string
  parentOrderUuid?: string
  // Snapshot des liaisons existantes en base : permet d'identifier les ajouts/retraits/modifs en attente.
  initialChildren?: any[]
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'update:selectedChildren', children: any[]): void
}>()

const quoteStore = useQuoteStore()
const $toast = useToast()

// --- State de recherche ---
const searchQuery = ref('')
const isSearching = ref(false)

// --- Résultat de recherche en attente de confirmation ---
// 'existing' = la commande existe déjà dans le système → on la lie en "indépendante" (imposé)
// 'create'   = aucune commande pour ce devis → l'utilisateur choisit formulaire + type de liaison
type PendingResult =
  | null
  | {
      kind: 'existing'
      quoteReference: string
      orderUuid: string
      signedAt: string | null
      signedManuallyAt: string | null
    }
  | {
      kind: 'create'
      quoteReference: string
      followParent: boolean
      // Utilisé uniquement quand followParent === false (commande indépendante).
      // Si followParent === true, on hérite automatiquement du formulaire de la commande principale.
      selectedFormType: string | null
    }
const pendingResult = ref<PendingResult>(null)

const canConfirm = computed(() => {
  if (!pendingResult.value) return false
  if (pendingResult.value.kind === 'existing') return true
  // En "Suit la principale", le formulaire est auto-hérité.
  // En "Indépendante", l'utilisateur doit obligatoirement choisir un formulaire.
  return pendingResult.value.followParent || !!pendingResult.value.selectedFormType
})

// --- Recherche ---
async function onSearch() {
  const query = searchQuery.value.trim().toUpperCase()
  if (!query) return

  if (
    (props.parentReference && query === props.parentReference.toUpperCase()) ||
    (props.parentOrderUuid && query === props.parentOrderUuid.toUpperCase())
  ) {
    $toast.error('Impossible de lier la commande principale à elle-même.')
    return
  }

  if (props.selectedChildren.some((c) =>
    c.quoteReference?.toUpperCase() === query ||
    (c.orderUuid && c.orderUuid.toUpperCase() === query)
  )) {
    $toast.info('Cette commande est déjà liée.')
    return
  }

  isSearching.value = true
  pendingResult.value = null

  try {
    const response = await quoteStore.fetchQuote(query)
    const payload = response?.data ?? response
    const quote = Array.isArray(payload) ? payload[0] : payload

    if (!quote || !quote.quoteReference) {
      $toast.error(`Aucun devis trouvé pour la référence "${query}".`)
      return
    }

    if (props.parentReference && quote.quoteReference.toUpperCase() === props.parentReference.toUpperCase()) {
      $toast.error('Impossible de lier la commande principale à elle-même.')
      return
    }

    const existingOrder = quote.order

    if (existingOrder && existingOrder.uuid) {
      if (props.parentOrderUuid && existingOrder.uuid === props.parentOrderUuid) {
        $toast.error('Impossible de lier la commande principale à elle-même.')
        return
      }
      pendingResult.value = {
        kind: 'existing',
        quoteReference: quote.quoteReference,
        orderUuid: existingOrder.uuid,
        signedAt: quote.signedAt ?? null,
        signedManuallyAt: quote.signedManuallyAt ?? null,
      }
    } else {
      pendingResult.value = {
        kind: 'create',
        quoteReference: quote.quoteReference,
        followParent: true,
        selectedFormType: null,
      }
    }
  } catch {
    $toast.error('Erreur lors de la recherche.')
  } finally {
    isSearching.value = false
  }
}

function confirmAdd() {
  if (!pendingResult.value) return
  if (pendingResult.value.kind === 'existing') {
    addChild({
      orderUuid: pendingResult.value.orderUuid,
      quoteReference: pendingResult.value.quoteReference,
      exists: true,
      followParentWorkflow: false,
      signedAt: pendingResult.value.signedAt,
      signedManuallyAt: pendingResult.value.signedManuallyAt,
    })
  } else {
    addChild({
      quoteReference: pendingResult.value.quoteReference,
      // Si "Suit la principale" → formulaire hérité du parent. Sinon, formulaire choisi explicitement.
      formId: pendingResult.value.followParent ? props.parentFormType : pendingResult.value.selectedFormType,
      exists: false,
      followParentWorkflow: pendingResult.value.followParent,
    })
  }
  searchQuery.value = ''
  pendingResult.value = null
  $toast.success('Commande liée ajoutée.')
}

function cancelPending() {
  pendingResult.value = null
  searchQuery.value = ''
}

// --- Helpers liste ---
function addChild(child: any) {
  emit('update:selectedChildren', [...props.selectedChildren, child])
}

function removeChild(child: any) {
  emit('update:selectedChildren', props.selectedChildren.filter((c) =>
    (c.orderUuid && c.orderUuid !== child.orderUuid) ||
    (!c.orderUuid && c.quoteReference !== child.quoteReference)
  ))
}

// --- Détection des modifications en attente (par rapport à l'état initial en base) ---
function childKey(c: any): string {
  return c.orderUuid || `q:${c.quoteReference}`
}

function getChildStatus(child: any): 'new' | 'modified' | 'unchanged' {
  if (!props.initialChildren) return 'unchanged'
  const orig = props.initialChildren.find((c) => childKey(c) === childKey(child))
  if (!orig) return 'new'
  if (orig.followParentWorkflow !== child.followParentWorkflow) return 'modified'
  return 'unchanged'
}

const removedChildrenCount = computed(() => {
  if (!props.initialChildren) return 0
  const currentKeys = new Set(props.selectedChildren.map(childKey))
  return props.initialChildren.filter((c) => !currentKeys.has(childKey(c))).length
})

const pendingChangesCount = computed(() => {
  const addedOrModified = props.selectedChildren.filter((c) => getChildStatus(c) !== 'unchanged').length
  return addedOrModified + removedChildrenCount.value
})

const hasPendingChanges = computed(() => pendingChangesCount.value > 0)

// Transition one-way uniquement : "Suit la principale" → "Indépendante".
// Une fois indépendante, la commande a son propre cycle de vie / ses propres données ;
// la repasser en synchro écraserait ces données, donc on ne le permet pas.
function setChildIndependent(child: any) {
  const updated = props.selectedChildren.map((c) => {
    const isSame = c.orderUuid ? c.orderUuid === child.orderUuid : c.quoteReference === child.quoteReference
    return isSame ? { ...c, followParentWorkflow: false } : c
  })
  emit('update:selectedChildren', updated)
}

// --- Reset de l'état local à chaque ouverture de la modal ---
// Le composant reste monté dans le parent donc son state persiste entre les ouvertures.
// On remet tout à zéro quand la modal s'ouvre pour repartir d'un état propre.
function resetLocalState() {
  searchQuery.value = ''
  isSearching.value = false
  pendingResult.value = null
}

watch(
  () => props.showModal,
  (isOpen, wasOpen) => {
    if (isOpen && !wasOpen) {
      resetLocalState()
    }
  },
)
</script>

<template>
  <Modal :show-modal="showModal" help-key="order-children" title="Commandes liées" max-width="3xl" @close="emit('close')">
    <template #content>
      <div class="mt-4 flex flex-col gap-6">

        <!-- Bandeau d'information : les modifs ne sont pas persistées tant qu'on n'a pas cliqué sur Enregistrer -->
        <div
          class="rounded-xl border p-3 flex items-start gap-3 transition-colors"
          :class="hasPendingChanges
            ? 'border-warning/40 bg-warning/5'
            : 'border-info/30 bg-info/5'"
        >
          <component
            :is="hasPendingChanges ? ExclamationTriangleIcon : InformationCircleIcon"
            class="w-5 h-5 shrink-0 mt-0.5"
            :class="hasPendingChanges ? 'text-warning' : 'text-info'"
          />
          <div class="text-sm flex-1 min-w-0">
            <p v-if="hasPendingChanges" class="font-medium">
              {{ pendingChangesCount }} modification{{ pendingChangesCount > 1 ? 's' : '' }} en attente
            </p>
            <p :class="hasPendingChanges ? 'text-xs text-base-content/70 mt-0.5' : ''">
              Les ajouts, retraits et changements de liaison seront appliqués lorsque vous cliquerez sur <strong>Enregistrer</strong> sur la commande.
            </p>
          </div>
        </div>

        <!-- ============================ -->
        <!-- Section : Ajouter une liaison -->
        <!-- ============================ -->
        <div v-if="!isFormReadOnly" class="rounded-2xl border-2 border-primary/20 bg-primary/5 p-4 flex flex-col gap-3">
          <div>
            <h3 class="font-semibold text-sm flex items-center gap-2">
              <PlusCircleIcon class="w-4 h-4 text-primary" />
              Ajouter une commande liée
            </h3>
            <p class="text-xs text-base-content/60 mt-0.5">Saisissez la référence du devis à lier à cette commande.</p>
          </div>

          <!-- Étape 1 : recherche -->
          <div v-if="!pendingResult" class="flex gap-2">
            <div class="relative flex-1">
              <MagnifyingGlassIcon class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-base-content/40 pointer-events-none" />
              <input
                v-model="searchQuery"
                type="text"
                class="input input-bordered w-full pl-9 pr-4"
                placeholder="Référence devis (ex: SO-1234)"
                @keyup.enter="onSearch"
              />
            </div>
            <ButtonDaisy
              text="Rechercher"
              variant="primary"
              :icon="isSearching ? ArrowPathIcon : MagnifyingGlassIcon"
              :loader="isSearching"
              :disabled="!searchQuery.trim()"
              @click="onSearch"
            />
          </div>

          <!-- Étape 2a : commande déjà existante → liaison comme indépendante -->
          <Transition
            enter-active-class="transition-all duration-200"
            enter-from-class="opacity-0 -translate-y-1"
            leave-active-class="transition-all duration-150"
            leave-to-class="opacity-0 -translate-y-1"
          >
            <div v-if="pendingResult?.kind === 'existing'" class="rounded-xl border-2 border-success/40 bg-success/5 p-4 flex flex-col gap-4">
              <div class="flex items-start gap-3">
                <CheckCircleIcon class="w-5 h-5 text-success shrink-0 mt-0.5" />
                <div class="flex-1 min-w-0">
                  <p class="font-semibold text-sm">
                    Commande <span class="text-success">{{ pendingResult.quoteReference }}</span> trouvée
                  </p>
                  <p class="text-xs text-base-content/70 mt-1">
                    Cette commande existe déjà avec son propre formulaire et son propre cycle de vie.
                    Elle sera liée en tant que <strong>commande indépendante</strong> : son statut ne sera pas synchronisé avec celui de la commande principale.
                  </p>
                </div>
              </div>
              <div class="flex justify-end gap-2">
                <ButtonDaisy text="Annuler" variant="ghost" size="sm" @click="cancelPending" />
                <ButtonDaisy
                  text="Confirmer la liaison"
                  variant="primary"
                  size="sm"
                  :icon="PlusCircleIcon"
                  @click="confirmAdd"
                />
              </div>
            </div>
          </Transition>

          <!-- Étape 2b : aucune commande → création inline (formulaire + type de liaison) -->
          <Transition
            enter-active-class="transition-all duration-200"
            enter-from-class="opacity-0 -translate-y-1"
            leave-active-class="transition-all duration-150"
            leave-to-class="opacity-0 -translate-y-1"
          >
            <div v-if="pendingResult?.kind === 'create'" class="rounded-xl border-2 border-warning/60 bg-warning/5 p-4 flex flex-col gap-5">
              <div class="flex items-start gap-3">
                <ExclamationTriangleIcon class="w-5 h-5 text-warning shrink-0 mt-0.5" />
                <div>
                  <p class="font-semibold text-sm">
                    Aucune commande pour <span class="text-warning">{{ pendingResult.quoteReference }}</span>
                  </p>
                  <p class="text-xs text-base-content/60 mt-0.5">Voulez-vous créer un brouillon de commande pour cette référence et le lier ?</p>
                </div>
              </div>

              <!-- 1. Type de liaison -->
              <div class="flex flex-col gap-2">
                <p class="text-sm font-medium flex items-center gap-2">
                  <span class="badge badge-neutral badge-sm">1</span>
                  Type de liaison
                </p>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    class="flex flex-col items-start gap-1 p-3 rounded-xl border-2 transition-all text-left"
                    :class="pendingResult.followParent
                      ? 'border-info bg-info/5 shadow-sm'
                      : 'border-base-300 hover:border-base-400'"
                    @click="pendingResult.followParent = true"
                  >
                    <div class="flex items-center gap-2">
                      <div class="w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors"
                        :class="pendingResult.followParent ? 'border-info' : 'border-base-400'"
                      >
                        <div v-if="pendingResult.followParent" class="w-2 h-2 rounded-full bg-info" />
                      </div>
                      <span class="font-semibold text-sm">Suit la commande principale</span>
                    </div>
                    <p class="text-xs text-base-content/50 ml-6">Statut synchronisé automatiquement avec celui de la commande principale.</p>
                  </button>

                  <button
                    type="button"
                    class="flex flex-col items-start gap-1 p-3 rounded-xl border-2 transition-all text-left"
                    :class="!pendingResult.followParent
                      ? 'border-primary bg-primary/5 shadow-sm'
                      : 'border-base-300 hover:border-base-400'"
                    @click="pendingResult.followParent = false"
                  >
                    <div class="flex items-center gap-2">
                      <div class="w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors"
                        :class="!pendingResult.followParent ? 'border-primary' : 'border-base-400'"
                      >
                        <div v-if="!pendingResult.followParent" class="w-2 h-2 rounded-full bg-primary" />
                      </div>
                      <span class="font-semibold text-sm">Commande indépendante</span>
                    </div>
                    <p class="text-xs text-base-content/50 ml-6">Cycle de vie et statut autonomes, indépendants de la commande principale.</p>
                  </button>
                </div>
              </div>

              <!-- 2. Formulaire -->
              <!-- Si "Suit la principale" : formulaire hérité automatiquement (info). -->
              <!-- Si "Indépendante" : l'utilisateur doit choisir explicitement. -->
              <div class="flex flex-col gap-2">
                <p class="text-sm font-medium flex items-center gap-2">
                  <span class="badge badge-neutral badge-sm">2</span>
                  Formulaire
                </p>
                <div
                  v-if="pendingResult.followParent"
                  class="flex items-start gap-2 p-3 rounded-lg bg-info/5 border border-info/20"
                >
                  <DocumentDuplicateIcon class="w-4 h-4 text-info shrink-0 mt-0.5" />
                  <p class="text-sm text-base-content/80">
                    Le formulaire de la commande principale sera utilisé
                    <span v-if="parentFormType" class="font-medium">
                      ({{ formTypeOptions.find(o => o.value === parentFormType)?.label || parentFormType }})
                    </span>.
                  </p>
                </div>
                <select
                  v-else
                  v-model="pendingResult.selectedFormType"
                  class="select select-bordered select-sm w-full"
                >
                  <option :value="null" disabled>Choisir un formulaire</option>
                  <option v-for="opt in formTypeOptions" :key="opt.id" :value="opt.value">{{ opt.label }}</option>
                </select>
              </div>

              <div class="flex justify-end gap-2">
                <ButtonDaisy text="Annuler" variant="ghost" size="sm" @click="cancelPending" />
                <ButtonDaisy
                  text="Créer la commande liée"
                  variant="primary"
                  size="sm"
                  :icon="PlusCircleIcon"
                  :disabled="!canConfirm"
                  @click="confirmAdd"
                />
              </div>
            </div>
          </Transition>
        </div>

        <!-- ============================ -->
        <!-- Section : Liste des liaisons -->
        <!-- ============================ -->
        <div>
          <div class="flex items-center justify-between mb-3">
            <p class="font-semibold text-sm flex items-center gap-2">
              <DocumentDuplicateIcon class="w-4 h-4 opacity-60" />
              Commandes liées
              <span v-if="selectedChildren.length" class="badge badge-neutral badge-sm">{{ selectedChildren.length }}</span>
            </p>
          </div>

          <div v-if="selectedChildren.length" class="flex flex-col gap-2">
            <div
              v-for="child in selectedChildren"
              :key="child.orderUuid || child.quoteReference"
              class="flex items-center gap-3 p-3 rounded-xl border border-base-300 bg-base-100 hover:bg-base-200/40 transition-colors group"
            >
              <!-- Icône statut commande -->
              <div class="shrink-0">
                <CheckCircleIcon v-if="child.exists" class="w-5 h-5 text-success" />
                <ExclamationTriangleIcon v-else class="w-5 h-5 text-warning" />
              </div>

              <!-- Infos -->
              <div class="flex-1 min-w-0">
                <!-- Référence cliquable si la commande existe -->
                <a
                  v-if="child.exists && child.orderUuid"
                  :href="`/dashboard/order/${child.orderUuid}`"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="font-semibold text-sm text-primary hover:underline inline-flex items-center gap-1"
                >
                  {{ child.quoteReference }}
                  <ArrowTopRightOnSquareIcon class="w-3.5 h-3.5 opacity-60 shrink-0" />
                </a>
                <p v-else class="font-semibold text-sm">{{ child.quoteReference }}</p>

                <div class="flex items-center gap-2 mt-1 flex-wrap">
                  <!-- Badge "modification en attente" par rapport à l'état en base -->
                  <span
                    v-if="getChildStatus(child) === 'new'"
                    class="badge badge-success badge-sm gap-1"
                    title="Cette liaison sera créée à l'enregistrement"
                  >
                    <span class="w-1.5 h-1.5 rounded-full bg-success-content/80" />
                    Nouveau
                  </span>
                  <span
                    v-else-if="getChildStatus(child) === 'modified'"
                    class="badge badge-warning badge-sm gap-1"
                    title="Le type de liaison a été modifié"
                  >
                    <span class="w-1.5 h-1.5 rounded-full bg-warning-content/80" />
                    Modifié
                  </span>

                  <!-- Statut commande -->
                  <span v-if="!child.exists" class="text-xs text-warning font-medium">
                    À créer · {{ formTypeOptions.find(o => o.value === child.formId)?.label || child.formId || '—' }}
                  </span>

                  <!-- Statut signature devis -->
                  <span
                    v-if="child.exists"
                    class="inline-flex items-center gap-1 text-xs font-medium"
                    :class="(child.signedAt || child.signedManuallyAt) ? 'text-success' : 'text-error'"
                  >
                    <ShieldCheckIcon v-if="child.signedAt || child.signedManuallyAt" class="w-3.5 h-3.5 shrink-0" />
                    <ShieldExclamationIcon v-else class="w-3.5 h-3.5 shrink-0" />
                    <span v-if="child.signedAt">
                      Signé le {{ new Date(child.signedAt).toLocaleDateString('fr-FR') }}
                    </span>
                    <span v-else-if="child.signedManuallyAt">
                      Signé manuellement le {{ new Date(child.signedManuallyAt).toLocaleDateString('fr-FR') }}
                    </span>
                    <span v-else>Devis non signé</span>
                  </span>
                </div>
              </div>

              <!-- Type de liaison : transition one-way "Suit la principale" → "Indépendante" -->
              <button
                v-if="child.followParentWorkflow"
                class="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border transition-all shrink-0 border-info/50 bg-info/10 text-info hover:bg-info/20"
                :disabled="isFormReadOnly"
                title="Rendre cette commande indépendante (action irréversible)"
                @click="!isFormReadOnly && setChildIndependent(child)"
              >
                <ArrowsRightLeftIcon class="w-3.5 h-3.5 shrink-0" />
                Suit la principale
              </button>
              <span
                v-else
                class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border border-base-300 bg-base-200/50 text-base-content/60 shrink-0"
                title="Cette commande a son propre cycle de vie"
              >
                Indépendante
              </span>

              <!-- Supprimer -->
              <button
                v-if="!isFormReadOnly"
                class="btn btn-ghost btn-xs btn-circle text-error opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                @click="removeChild(child)"
              >
                <XMarkIcon class="w-4 h-4" />
              </button>
            </div>
          </div>

          <div v-else class="flex flex-col items-center justify-center py-10 rounded-xl border-2 border-dashed border-base-300 bg-base-200/20">
            <DocumentDuplicateIcon class="w-10 h-10 opacity-15 mb-3" />
            <p class="text-sm font-medium opacity-40">Aucune commande liée</p>
            <p v-if="!isFormReadOnly" class="text-xs opacity-30 mt-1">Saisissez une référence devis ci-dessus pour en ajouter une</p>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex justify-end pt-1">
          <ButtonDaisy text="Fermer" variant="ghost" @click="emit('close')" />
        </div>

      </div>
    </template>
  </Modal>
</template>
